import React from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'April 15, 2026';

const SECTIONS = [
  {
    id: 'overview',
    title: '1. Overview',
    body: [
      'Lexint ("we", "us", "our") operates the DepGuard, Vigil, and PRISM software products and this website at lexint.io. This Privacy Policy explains how we collect, use, and protect information when you use our website and products.',
      'We are committed to protecting your privacy and complying with the EU General Data Protection Regulation (GDPR), the EU Cyber Resilience Act (CRA), and applicable national data protection laws.',
    ],
  },
  {
    id: 'controller',
    title: '2. Data controller',
    body: [
      'Lexint B.V. is the data controller for personal data collected through this website and the platform portal. For privacy-related enquiries, contact us at privacy@lexint.io.',
    ],
  },
  {
    id: 'what-we-collect',
    title: '3. What we collect',
    subsections: [
      {
        heading: '3.1 Website visitors',
        points: [
          'IP address and approximate geolocation (anonymised after 24 hours)',
          'Browser type, operating system, referring URL',
          'Pages visited and time spent (aggregated, no cross-site tracking)',
          'Contact form submissions: name, email, company, message',
        ],
      },
      {
        heading: '3.2 Platform users (DepGuard / Vigil / PRISM)',
        points: [
          'Account information: name, work email address, organisation name',
          'Authentication tokens (JWT, stored client-side only)',
          'Audit log entries: actions performed, timestamps, user ID',
          'SBOM data and scan results — processed locally in your infrastructure (Enterprise on-premises) or in your private tenant (SaaS)',
        ],
      },
      {
        heading: '3.3 What we do NOT collect',
        points: [
          'We do not sell your data to third parties — ever',
          'We do not collect source code or SBOM contents on Enterprise on-premises plans',
          'We do not use cross-site advertising trackers',
          'We do not collect sensitive personal data (health, financial, biometric)',
        ],
      },
    ],
  },
  {
    id: 'legal-basis',
    title: '4. Legal basis for processing',
    table: [
      { purpose: 'Providing the software service', basis: 'Performance of contract (Art. 6(1)(b) GDPR)' },
      { purpose: 'Sending product updates and alerts', basis: 'Legitimate interest (Art. 6(1)(f) GDPR)' },
      { purpose: 'Marketing communications', basis: 'Consent (Art. 6(1)(a) GDPR)' },
      { purpose: 'Security monitoring and fraud prevention', basis: 'Legitimate interest (Art. 6(1)(f) GDPR)' },
      { purpose: 'Legal compliance and audit', basis: 'Legal obligation (Art. 6(1)(c) GDPR)' },
    ],
  },
  {
    id: 'cookies',
    title: '5. Cookies',
    body: [
      'We use cookies and similar technologies to operate the website and platform. Essential cookies are required for the service to function (authentication, session management). Non-essential cookies (analytics) are only set after you give consent via our cookie consent banner.',
    ],
    table: [
      { name: 'auth_token', purpose: 'JWT authentication token', duration: 'Session', essential: true },
      { name: 'cookie_consent', purpose: 'Stores your cookie preferences', duration: '1 year', essential: true },
      { name: '_plausible_*', purpose: 'Privacy-friendly page view analytics (no cross-site tracking)', duration: 'Session', essential: false },
    ],
  },
  {
    id: 'data-sharing',
    title: '6. Data sharing and third parties',
    body: [
      'We share personal data only where strictly necessary:',
    ],
    points: [
      'OSV.dev and deps.dev — package vulnerability and health lookups (package names/versions only, no personal data)',
      'OpenSSF Scorecard API — GitHub repository health queries (repo names only)',
      'Email delivery provider — for transactional emails (account confirmation, alerts)',
      'We do not use Google Analytics, Facebook Pixel, or any advertising network',
    ],
  },
  {
    id: 'retention',
    title: '7. Data retention',
    body: [
      'We retain personal data only as long as necessary for the purposes described in this policy:',
    ],
    table: [
      { category: 'Account information', retention: '5 years after account closure' },
      { category: 'Audit log entries', retention: '10 years (required by EU CRA Article 20)' },
      { category: 'Contact form submissions', retention: '2 years' },
      { category: 'Website analytics (anonymised)', retention: '13 months' },
      { category: 'Support tickets', retention: '3 years after closure' },
    ],
  },
  {
    id: 'your-rights',
    title: '8. Your rights under GDPR',
    points: [
      'Right of access — request a copy of the personal data we hold about you',
      'Right to rectification — correct inaccurate or incomplete data',
      'Right to erasure — request deletion of your personal data ("right to be forgotten")',
      'Right to restrict processing — limit how we use your data in certain circumstances',
      'Right to data portability — receive your data in a structured, machine-readable format',
      'Right to object — object to processing based on legitimate interests',
      'Right to withdraw consent — withdraw consent for marketing communications at any time',
    ],
    body: [
      'To exercise your rights, email privacy@lexint.io. We will respond within 30 days. You also have the right to lodge a complaint with your national supervisory authority.',
    ],
  },
  {
    id: 'security',
    title: '9. Data security',
    body: [
      'We implement appropriate technical and organisational measures to protect personal data against accidental loss, unauthorised access, disclosure, alteration, or destruction. These include JWT-based authentication, RBAC access controls, TLS encryption in transit, and regular security reviews.',
      'For Enterprise on-premises deployments, all data remains within your infrastructure. Lexint has no access to your SBOM data, scan results, or audit logs.',
    ],
  },
  {
    id: 'international',
    title: '10. International transfers',
    body: [
      'Personal data is processed within the European Economic Area (EEA). Where we use third-party services that may process data outside the EEA, we ensure appropriate safeguards are in place (Standard Contractual Clauses or adequacy decision).',
    ],
  },
  {
    id: 'changes',
    title: '11. Changes to this policy',
    body: [
      'We may update this Privacy Policy from time to time. We will notify registered users of material changes by email at least 30 days before they take effect. The "last updated" date at the top of this page reflects the most recent revision.',
    ],
  },
  {
    id: 'contact',
    title: '12. Contact',
    body: [
      'For privacy-related questions, data subject requests, or to report a concern, contact our privacy team at privacy@lexint.io or write to: Lexint B.V., Privacy Team, The Netherlands.',
    ],
  },
];

export default function WebsitePrivacyPage() {
  return (
    <div className="dgw-page-wrap">
      {/* Hero */}
      <section style={{background:'#0f172a', padding:'80px 24px 60px', textAlign:'center'}}>
        <div style={{maxWidth:760, margin:'0 auto'}}>
          <div className="dgw-badge" style={{margin:'0 auto 20px'}}>Legal</div>
          <h1 style={{fontSize:'clamp(28px,4vw,48px)', fontWeight:800, color:'#f1f5f9', margin:'0 0 16px', letterSpacing:'-1px'}}>
            Privacy Policy
          </h1>
          <p style={{color:'#94a3b8', fontSize:16, margin:'0 0 12px'}}>
            Last updated: {LAST_UPDATED}
          </p>
          <p style={{color:'#64748b', fontSize:14}}>
            This policy explains how Lexint collects, uses, and protects your personal data.
          </p>
        </div>
      </section>

      {/* Content */}
      <section style={{background:'#fff', padding:'64px 24px'}}>
        <div className="dgw-sidebar-layout" style={{maxWidth:800, margin:'0 auto'}}>
          {/* Sidebar TOC */}
          <div className="dgw-toc" style={{position:'sticky', top:100}}>
            <div style={{fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:1, marginBottom:12}}>Contents</div>
            <nav style={{display:'flex', flexDirection:'column', gap:8}}>
              {SECTIONS.map(s => (
                <a key={s.id} href={`#${s.id}`} style={{fontSize:13, color:'#64748b', textDecoration:'none', lineHeight:1.4}}
                  onMouseOver={e => e.target.style.color='#6366f1'}
                  onMouseOut={e => e.target.style.color='#64748b'}
                >{s.title}</a>
              ))}
            </nav>
          </div>

          {/* Main content */}
          <div style={{display:'flex', flexDirection:'column', gap:48}}>
            {SECTIONS.map(s => (
              <div key={s.id} id={s.id}>
                <h2 style={{fontSize:20, fontWeight:700, color:'#0f172a', margin:'0 0 16px', paddingBottom:12, borderBottom:'1px solid #f1f5f9'}}>{s.title}</h2>

                {s.body && s.body.map((p, i) => (
                  <p key={i} style={{fontSize:15, color:'#475569', lineHeight:1.75, margin:'0 0 12px'}}>{p}</p>
                ))}

                {s.subsections && s.subsections.map(sub => (
                  <div key={sub.heading} style={{marginBottom:20}}>
                    <h3 style={{fontSize:15, fontWeight:700, color:'#334155', margin:'0 0 10px'}}>{sub.heading}</h3>
                    <ul style={{margin:0, paddingLeft:20, display:'flex', flexDirection:'column', gap:6}}>
                      {sub.points.map(p => (
                        <li key={p} style={{fontSize:14, color:'#475569', lineHeight:1.6}}>{p}</li>
                      ))}
                    </ul>
                  </div>
                ))}

                {s.points && (
                  <ul style={{margin:'0 0 12px', paddingLeft:20, display:'flex', flexDirection:'column', gap:6}}>
                    {s.points.map(p => (
                      <li key={p} style={{fontSize:14, color:'#475569', lineHeight:1.6}}>{p}</li>
                    ))}
                  </ul>
                )}

                {s.table && s.id === 'legal-basis' && (
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:14}}>
                    <thead><tr>
                      {['Purpose', 'Legal basis'].map(h => <th key={h} style={{textAlign:'left', padding:'10px 12px', background:'#f8fafc', borderBottom:'2px solid #e2e8f0', color:'#64748b', fontWeight:700, fontSize:12, textTransform:'uppercase', letterSpacing:.5}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{s.table.map((r, i) => (
                      <tr key={i} style={{background: i%2===0?'#fff':'#f8fafc'}}>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', color:'#334155'}}>{r.purpose}</td>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', color:'#6366f1', fontWeight:600}}>{r.basis}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}

                {s.table && s.id === 'cookies' && (
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:13, marginTop:12}}>
                    <thead><tr>
                      {['Cookie', 'Purpose', 'Duration', 'Type'].map(h => <th key={h} style={{textAlign:'left', padding:'10px 12px', background:'#f8fafc', borderBottom:'2px solid #e2e8f0', color:'#64748b', fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:.5}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{s.table.map((r, i) => (
                      <tr key={i} style={{background: i%2===0?'#fff':'#f8fafc'}}>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', fontFamily:'monospace', color:'#334155'}}>{r.name}</td>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', color:'#475569'}}>{r.purpose}</td>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', color:'#64748b'}}>{r.duration}</td>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9'}}>
                          <span style={{fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:100,
                            background: r.essential ? 'rgba(16,185,129,.1)' : 'rgba(245,158,11,.1)',
                            color: r.essential ? '#065f46' : '#78350f',
                            border: `1px solid ${r.essential ? 'rgba(16,185,129,.25)' : 'rgba(245,158,11,.25)'}`,
                          }}>{r.essential ? 'Essential' : 'Analytics'}</span>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}

                {s.table && s.id === 'retention' && (
                  <table style={{width:'100%', borderCollapse:'collapse', fontSize:14, marginTop:12}}>
                    <thead><tr>
                      {['Data category', 'Retention period'].map(h => <th key={h} style={{textAlign:'left', padding:'10px 12px', background:'#f8fafc', borderBottom:'2px solid #e2e8f0', color:'#64748b', fontWeight:700, fontSize:12, textTransform:'uppercase', letterSpacing:.5}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{s.table.map((r, i) => (
                      <tr key={i} style={{background: i%2===0?'#fff':'#f8fafc'}}>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', color:'#334155', fontWeight:600}}>{r.category}</td>
                        <td style={{padding:'12px', borderBottom:'1px solid #f1f5f9', color:'#475569'}}>{r.retention}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <section style={{background:'#f8fafc', padding:'40px 24px', textAlign:'center'}}>
        <p style={{color:'#64748b', fontSize:14, margin:'0 0 16px'}}>
          Questions about this policy? We reply within 2 business days.
        </p>
        <div style={{display:'flex', gap:12, justifyContent:'center'}}>
          <Link to="/website/contact" className="dgw-btn dgw-btn-primary">Contact privacy team</Link>
          <Link to="/website/terms" className="dgw-btn dgw-btn-ghost">Terms of Service</Link>
        </div>
      </section>
    </div>
  );
}
